import React, { useState, useEffect, Suspense, lazy } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { Terminal, ExternalLink, Shield, Package, Users, Key } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import Navbar from './Navbar'; 
import LoadingScreen from './LoadingScreen'; 
import { Reveal, RevealGroup, RevealItem } from './Reveal'; 

// --- LAZY LOADED COMPONENTS ---
const WebBuilder = lazy(() => import('./WebBuilder'));
const CoreStackChat = lazy(() => import('./CoreStackChat'));
const LivingCodeBackground = lazy(() => import('./LivingCodeBackground'));
const MinimalCursor = lazy(() => import('./MinimalCursor'));
// Import LandingPage and sub-sections for focused views
import LandingPage, { ServicesSection, ProjectsSection, ContactSection, AboutSection } from './LandingPage';

// --- ANIMATION CONFIGURATION (TURBO MODE) ---
// Tuned for "Slippery/Instant" feel
const pageVariants: Variants = {
  initial: { 
    opacity: 0, 
    y: 10,
    filter: 'blur(2px)' 
  },
  animate: { 
    opacity: 1, 
    y: 0, 
    filter: 'blur(0px)',
    transition: {
      type: "spring",
      stiffness: 300,   // Very High stiffness = Instant snap
      damping: 25,      // Controlled damping = No wobble
      mass: 0.5,        // Light mass
      duration: 0.3     // Fallback duration
    }
  },
  exit: { 
    opacity: 0, 
    y: -10, 
    filter: 'blur(2px)',
    transition: {
      duration: 0.15, // Ultra fast exit
      ease: "easeIn"
    }
  }
};

// Reusable Wrapper for Consistent Transitions
const PageWrapper: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <motion.div
    variants={pageVariants}
    initial="initial"
    animate="animate"
    exit="exit"
    className={`w-full min-h-[calc(100vh-80px)] gpu-accelerated ${className}`}
  >
    {children}
  </motion.div>
);

// --- INLINE COMPONENTS ---

const SoftwareDepot: React.FC = () => {
  const { t } = useLanguage();
  const products = [
    { id: "HR_001", name: "HR_TimeAttendance_Bot", version: "v2.1.0", description: t.depot.prod1_desc, price: "15,000", tech: ["Node.js", "Line API", "Firebase"], icon: <Users size={32} /> },
    { id: "INV_LITE", name: "Inventory_Tracker_Lite", version: "v1.0.4", description: t.depot.prod2_desc, price: "8,500", tech: ["React", "Supabase", "Tailwind"], icon: <Package size={32} /> },
    { id: "IOT_LOG", name: "ESP32_DataLogger_Kit", version: "v3.0.0", description: t.depot.prod3_desc, price: "2,500", tech: ["C++", "Google Scripts", "ESP32"], icon: <Terminal size={32} /> }
  ];

  return (
    <section className="py-24 bg-slate-50/80 dark:bg-[#070707]/80 border-b border-slate-200 dark:border-white/10 transition-colors duration-300 backdrop-blur-sm min-h-screen">
        <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <Reveal>
                      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white flex items-center gap-3 mb-2">
                          <span className="text-emerald-600 dark:text-terminal-green">./</span>{t.depot.headline}
                      </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                      <p className="text-slate-600 dark:text-gray-500 font-mono text-sm">{t.depot.subhead}</p>
                    </Reveal>
                </div>
                <Reveal delay={0.2}>
                  <div className="flex items-center gap-2 text-cyan-700 dark:text-terminal-cyan text-xs font-mono border border-cyan-700/30 dark:border-terminal-cyan/30 px-3 py-1 bg-cyan-100 dark:bg-terminal-cyan/5 rounded">
                      <Shield size={14} /> {t.depot.secure_badge}
                  </div>
                </Reveal>
            </div>

            <RevealGroup className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {products.map((product) => (
                    <RevealItem key={product.id}>
                      <motion.div
                          whileHover={{ y: -4 }}
                          className="bg-white dark:bg-[#0b0b0b] border border-slate-200 dark:border-white/10 relative group overflow-hidden shadow-sm hover:shadow-lg dark:hover:shadow-none transition-all duration-300 h-full"
                      >
                           <div className="absolute top-0 left-0 w-2 h-2 border-l-2 border-t-2 border-emerald-600/50 dark:border-terminal-green/50"></div>
                           <div className="absolute top-0 right-0 w-2 h-2 border-r-2 border-t-2 border-emerald-600/50 dark:border-terminal-green/50"></div>
                           <div className="absolute bottom-0 left-0 w-2 h-2 border-l-2 border-b-2 border-emerald-600/50 dark:border-terminal-green/50"></div>
                           <div className="absolute bottom-0 right-0 w-2 h-2 border-r-2 border-b-2 border-emerald-600/50 dark:border-terminal-green/50"></div>

                           <div className="p-6 h-full flex flex-col relative z-10">
                              <div className="flex justify-between items-start mb-6">
                                  <div className="p-3 bg-slate-100 dark:bg-white/5 text-emerald-600 dark:text-terminal-green border border-slate-200 dark:border-white/10 rounded-sm">
                                      {product.icon}
                                  </div>
                                  <span className="text-[10px] text-slate-500 dark:text-gray-500 font-mono border border-slate-200 dark:border-gray-800 px-2 py-1 h-fit">{product.version}</span>
                              </div>

                              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 group-hover:text-emerald-700 dark:group-hover:text-terminal-green transition-colors">{product.name}</h3>
                              <p className="text-slate-600 dark:text-gray-400 text-sm mb-6 flex-grow leading-relaxed">{product.description}</p>
                              <div className="flex flex-wrap gap-2 mb-6">
                                  {product.tech.map(t => (
                                      <span key={t} className="text-[10px] text-slate-500 dark:text-gray-500 bg-slate-100 dark:bg-black px-2 py-1 border border-slate-200 dark:border-white/10">{t}</span>
                                  ))}
                              </div>
                              <div className="mt-auto border-t border-slate-100 dark:border-white/10 pt-4">
                                  <div className="flex justify-between items-center mb-4 font-mono">
                                      <span className="text-slate-500 dark:text-gray-500 text-xs">{t.depot.cost}</span>
                                      <span className="text-cyan-700 dark:text-terminal-cyan font-bold">[ {product.price} THB ]</span>
                                  </div>
                                  <button className="w-full bg-slate-100 dark:bg-white/5 hover:bg-emerald-600 dark:hover:bg-terminal-green hover:text-white dark:hover:text-black text-slate-700 dark:text-white py-3 text-xs font-bold uppercase tracking-wider border border-slate-200 dark:border-white/10 hover:border-emerald-600 dark:hover:border-terminal-green transition-all flex items-center justify-center gap-2 group-hover:shadow-[0_0_15px_rgba(57,255,20,0.15)]">
                                      <Key size={14} />
                                      {'>'} {t.depot.btn_inject}
                                  </button>
                              </div>
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-100/50 dark:via-terminal-green/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                      </motion.div>
                    </RevealItem>
                ))}
            </RevealGroup>
        </div>
    </section>
  );
};

interface FooterProps {
  onNavigate: (view: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const { t } = useLanguage();
  
  // Helper to clean decorative prefixes from titles for the sitemap
  const cleanTitle = (text: string) => text.replace(/^[\/\.\s]+/, '');

  const sitemapData = [
    {
      header: "NAVIGATION",
      links: [
        { label: cleanTitle(t.nav.home), view: "home" },
        { label: cleanTitle(t.nav.services), view: "services" },
        { label: cleanTitle(t.nav.builder), view: "builder" },
        { label: cleanTitle(t.nav.projects), view: "projects" },
        { label: cleanTitle(t.nav.depot), view: "store" },
        { label: cleanTitle(t.nav.contact), view: "contact" },
      ]
    },
    {
      header: "SYSTEMS",
      links: [
        { label: cleanTitle(t.about.title), view: "about" },
        { label: cleanTitle(t.services.title), view: "services" },
        { label: cleanTitle(t.consulting.headline), view: "services" },
        { label: cleanTitle(t.techstack.headline), view: "about" }, 
      ]
    },
    {
      header: "RESOURCES",
      links: [
        { label: "Helpdesk Pro", href: "https://helpdesk.corestackdev.com" },
        { label: "IoT Monitor", href: "https://realtime.corestackdev.com" },
        { label: "Report Gen", href: "https://report.corestackdev.com" },
      ]
    }
  ];

  return (
    <footer className="bg-slate-100/90 dark:bg-black/90 border-t border-slate-200 dark:border-white/10 text-xs font-mono transition-colors duration-300 backdrop-blur-md content-visibility-auto">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="col-span-1 md:col-span-1 space-y-4">
             <div className="flex items-center gap-2">
                <Terminal size={24} className="text-emerald-600 dark:text-terminal-green" />
                <span className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">CORESTACK</span>
             </div>
             <p className="text-slate-500 dark:text-gray-500 leading-relaxed">
               {t.hero.subhead}
             </p>
             <div className="pt-2 flex gap-3">
                <div className="w-8 h-8 rounded bg-slate-200 dark:bg-white/5 flex items-center justify-center hover:bg-emerald-600 dark:hover:bg-terminal-green hover:text-white dark:hover:text-black transition-colors cursor-pointer">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
                </div>
                <div className="w-8 h-8 rounded bg-slate-200 dark:bg-white/5 flex items-center justify-center hover:bg-emerald-600 dark:hover:bg-terminal-green hover:text-white dark:hover:text-black transition-colors cursor-pointer">
                   {/* GitHub Icon */}
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.599 3.003-1.234 0-.853-.291-1.425-.771-1.764z"/></svg>
                </div>
             </div>
          </div>

          {/* Links Columns */}
          {sitemapData.map((section) => (
            <div key={section.header} className="col-span-1">
              <h4 className="font-bold text-slate-900 dark:text-white mb-6 tracking-widest">{section.header}</h4>
              <ul className="space-y-3">
                {section.links.map((link, idx) => (
                  <li key={idx}>
                    {link.view ? (
                      <button 
                        onClick={() => onNavigate(link.view!)}
                        className="text-slate-500 dark:text-gray-500 hover:text-emerald-600 dark:hover:text-terminal-green transition-colors text-left"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <a 
                        href={link.href} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-slate-500 dark:text-gray-500 hover:text-emerald-600 dark:hover:text-terminal-green transition-colors flex items-center gap-1 group"
                      >
                        {link.label}
                        <ExternalLink size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-slate-200 dark:border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-slate-400 dark:text-gray-600">
           <p>{t.footer.copyright}</p>
           <p className="flex items-center gap-2">
             <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
             {t.footer.status}
           </p>
        </div>
      </div>
    </footer>
  );
};

// --- APP COMPONENT ---

const App: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home');

  useEffect(() => {
    // Simulate initial loading
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleNavigate = (view: string) => {
    setCurrentView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#050505] text-slate-900 dark:text-white transition-colors duration-300 overflow-x-hidden font-sans selection:bg-emerald-500/30">
       <Suspense fallback={null}>
         <LivingCodeBackground />
         <MinimalCursor />
       </Suspense>

       <Navbar currentView={currentView} onNavigate={handleNavigate} />

       <main className="relative z-10 pt-16">
         <AnimatePresence mode="wait">
           {currentView === 'home' && (
             <PageWrapper key="home">
               <LandingPage onNavigate={handleNavigate} />
             </PageWrapper>
           )}
           {currentView === 'services' && (
             <PageWrapper key="services">
                <div className="pt-10">
                  <ServicesSection />
                  <AboutSection />
                </div>
             </PageWrapper>
           )}
           {currentView === 'builder' && (
             <PageWrapper key="builder">
                <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading Builder...</div>}>
                   <WebBuilder />
                </Suspense>
             </PageWrapper>
           )}
           {currentView === 'projects' && (
             <PageWrapper key="projects">
                <div className="pt-10">
                  <ProjectsSection />
                </div>
             </PageWrapper>
           )}
           {currentView === 'store' && (
             <PageWrapper key="store">
                <SoftwareDepot />
             </PageWrapper>
           )}
           {currentView === 'contact' && (
             <PageWrapper key="contact">
                <div className="pt-10">
                  <ContactSection />
                </div>
             </PageWrapper>
           )}
           {currentView === 'about' && (
             <PageWrapper key="about">
                <div className="pt-10">
                   <AboutSection />
                   <ServicesSection />
                </div>
             </PageWrapper>
           )}
         </AnimatePresence>
       </main>

       <Footer onNavigate={handleNavigate} />

       <Suspense fallback={null}>
         <CoreStackChat />
       </Suspense>
    </div>
  );
};

export default App;